import React, { useEffect, useRef, useState } from 'react';
import { Button, message, Progress, Icon } from 'antd';
import { useTranslation } from 'react-i18next';
import { CloseOutlined } from '@ant-design/icons';

const BulkOperationStatus = {
  Processing: 'processing',
  ManualConfirm: 'manualConfirm',
  Glitch: 'glitch',
  Failed: 'failed',
  Success: 'success',
};

function BulkOperationResultModal({ onClose, handlePromise, label }) {
  const { t } = useTranslation();
  const timerId = useRef();

  const [processBarPercent, setProcessBarPercent] = useState(0);
  const [status, setStatus] = useState(BulkOperationStatus.Processing);

  const stopProcessBar = () => {
    clearInterval(timerId.current);
    setProcessBarPercent(0);
  };

  const calcProcessBar = () => {
    setProcessBarPercent(current => (current + 1 < 99 ? current + 1 : current));
  };

  const startProcessBar = () => {
    const intervalID = window.setInterval(calcProcessBar, 100);
    timerId.current = intervalID;
  };

  const startProcess = async () => {
    try {
      setStatus(BulkOperationStatus.Processing);
      startProcessBar();
      await handlePromise();
      setStatus(BulkOperationStatus.Success);
    } catch (error) {
      message.error(error.message);
      stopProcessBar();
      setStatus(BulkOperationStatus.Failed);
    }
  };

  useEffect(() => {
    startProcess();
    return () => stopProcessBar();
  }, []);

  useEffect(() => {
    if (status !== BulkOperationStatus.Processing) {
      stopProcessBar();
    }
  }, [status]);

  useEffect(() => {
    if (processBarPercent >= 99) {
      stopProcessBar();
    }
  }, [processBarPercent]);

  return (
    <div className="properties-bulk-operation-container">
      <div className="properties-bulk-operation-container__header">
        <Choose>
          <When condition={status === BulkOperationStatus.Processing}>
            {t('cms.properties_bulk_operation.modal.title.processing')}
          </When>
          <When condition={status === BulkOperationStatus.ManualConfirm}>
            {t('cms.properties_bulk_operation.modal.title.need_confirm')}
          </When>
          <Otherwise>{t('cms.properties_bulk_operation.modal.title.result')}</Otherwise>
        </Choose>
        <CloseOutlined
          className="properties-bulk-operation-container__close-button"
          onClick={onClose}
        />
      </div>
      <div className="properties-bulk-operation-container__body">
        <section className="properties-bulk-operation-container__section">
          <Choose>
            <When condition={status === BulkOperationStatus.Processing}>
              <Progress percent={processBarPercent} showInfo />
            </When>
            <When condition={status === BulkOperationStatus.Glitch}>
              <div className="properties-bulk-operation-container__tooltip">
                <Icon
                  type="warning"
                  theme="filled"
                  className="properties-bulk-operation-container__tooltip__icon properties-bulk-operation-container__tooltip__icon--warning"
                />
                <p className="properties-bulk-operation-container__tooltip__title">
                  {label.glitch}
                </p>
              </div>
            </When>
            <When condition={status === BulkOperationStatus.Failed}>
              <div className="properties-bulk-operation-container__tooltip">
                <Icon
                  type="close-circle"
                  theme="filled"
                  className="properties-bulk-operation-container__tooltip__icon properties-bulk-operation-container__tooltip__icon--danger"
                />
                <p className="properties-bulk-operation-container__tooltip__title">
                  {label.failed}
                </p>
              </div>
            </When>
            <When condition={status === BulkOperationStatus.Success}>
              <div className="properties-bulk-operation-container__tooltip">
                <Icon
                  type="check-circle"
                  theme="filled"
                  className="properties-bulk-operation-container__tooltip__icon properties-bulk-operation-container__tooltip__icon--success"
                />
                <p className="properties-bulk-operation-container__tooltip__title">
                  {label.success}
                </p>
              </div>
            </When>
          </Choose>
        </section>
      </div>
      {status !== BulkOperationStatus.Processing ? (
        <div className="properties-bulk-operation-container__footer">
          {status === BulkOperationStatus.ManualConfirm ? (
            <Button
              size="large"
              className="properties-bulk-operation-container__cancel-button"
              onClick={onClose}
            >
              {t('cms.properties_bulk_operation.action.btn.cancel')}
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              className="properties-bulk-operation-container__save-button"
              onClick={onClose}
            >
              {t('cms.properties_bulk_operation.action.btn.got')}
            </Button>
          )}

          <If condition={status === BulkOperationStatus.ManualConfirm}>
            <Button
              type="primary"
              size="large"
              className="properties-bulk-operation-container__save-button"
              loading={bulkUnpublishPropertiesLoading}
              onClick={() =>
                handleSubmit(
                  bulkOperation,
                  true,
                  currentSelectedProperties,
                  invalidUnpublishedProperties.map(i => i.id),
                )
              }
            >
              {t('cms.properties_bulk_operation.action.btn.confirm')}
            </Button>
          </If>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

export default BulkOperationResultModal;
